import { useAtom } from 'jotai';

import { navbarClosedByUserState } from '@/features/navbar/navbar.state';
import { useCurrentUser } from '@/user/use-current-user.query';
import type { Sidebar } from '../state/sidebar.state';
import { sidebarState } from '../state/sidebar.state';
import { useActionPanelSearchParams } from '@/features/action-panel/use-action-panel-search-params';

export const useSidebars = () => {
  const [sidebar, setCurrentSidebar] = useAtom(sidebarState);
  const [navbarClosedByUser, setNavbarClosedByUser] = useAtom(
    navbarClosedByUserState,
  );
  const { data: user } = useCurrentUser();
  const userId = user?.id;
  const resetSidebar = () => setCurrentSidebar(null);
  const { resetActionPanelSearchParams } = useActionPanelSearchParams();

  const isActionPanelOpen = sidebar === 'ActionsPanel';

  const isNavbarClosedForUser = userId
    ? (navbarClosedByUser[userId] ?? false)
    : false;

  const isNavbarOpen = Boolean(userId && !isNavbarClosedForUser);

  const toggleSelectedSidebar = (selectedSidebar: Sidebar) => {
    if (sidebar === selectedSidebar) {
      return setCurrentSidebar(null);
    }
    setCurrentSidebar(selectedSidebar);
  };

  const toggleNavbar = () => {
    if (!userId) {
      return;
    }
    const currentlyClosed = navbarClosedByUser[userId] ?? false;
    setNavbarClosedByUser({
      ...navbarClosedByUser,
      [userId]: !currentlyClosed,
    });
  };
  const toggleSectionsPane = () => toggleSelectedSidebar('SectionsPane');
  const toggleActionPanel = () => {
    toggleSelectedSidebar('ActionsPanel');
    if (isActionPanelOpen) {
      resetActionPanelSearchParams();
    }
  };
  const toggleProjectSidebar = () => toggleSelectedSidebar('ProjectSidebar');
  const toggleTemplatesSidebar = () =>
    toggleSelectedSidebar('TemplatesSidebar');
  const toggleNotificationsSidebar = () =>
    toggleSelectedSidebar('Notifications');

  const isOpened = (sidebarName: Sidebar) => {
    return sidebar === sidebarName;
  };

  return {
    resetSidebar,
    isOpened,
    toggleNavbar,
    toggleSectionsPane,
    toggleActionPanel,
    toggleProjectSidebar,
    toggleTemplatesSidebar,
    toggleNotificationsSidebar,
    setCurrentSidebar,
    isNavbarOpen,
    isSectionsPaneOpen: sidebar === 'SectionsPane',
    isProjectSidebarOpen: sidebar === 'ProjectSidebar',
    isTemplatesSidebarOpen: sidebar === 'TemplatesSidebar',
    isNotificationsSidebarOpen: sidebar === 'Notifications',
    isActionPanelOpen,
  };
};
